import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { environment } from '@/environments/environment';
import { TranslateDynamicService } from '@services/translate-dynamic.service';

/**
 * Balises déclarées sur une route, dans `data.seo`.
 * Réservé aux pages dont le titre et la description ne dépendent pas de données
 * distantes ; les pages de détail posent elles-mêmes leurs balises.
 */
export interface SeoRouteData {
	titleKey: string;
	descriptionKey: string;
	noindex?: boolean;
}

export interface SeoPage {
	/** Titre de l'onglet. Préfixé par « Team Peps - » s'il ne le contient pas déjà. */
	title: string;
	description: string;
	/** Chemin de la page, avec slash initial (ex. `/news/42`). Sert au canonical et à og:url. */
	path: string;
	/** URL absolue de l'image de partage. Le logo Team Peps sert de repli. */
	image?: string;
	type?: 'website' | 'article' | 'profile';
	/** Date ISO de publication, pour les articles. */
	publishedTime?: string;
	/** Retire la page de l'index (pages de compte, tunnels, maintenance). */
	noindex?: boolean;
}

const SITE_NAME = 'Team Peps';
const TITLE_PREFIX = 'Team Peps';
// Dérivé 1200x630 de la bannière de marque, réservé au référencement.
//
// Deux raisons de ne pas pointer directement `banner.jpg` (1500x500, utilisée
// en en-tête des profils) : son ratio 3:1 serait recadré par les plateformes,
// qui rogneraient les logos partenaires ; et un format non JPEG/PNG comme
// l'AVIF ne serait décodé par aucun robot social.
const DEFAULT_IMAGE_PATH = '/assets/images/seo/og-banner.jpg';
const MAX_DESCRIPTION_LENGTH = 300;

@Injectable({ providedIn: 'root' })
export class SeoService {

	private readonly document = inject(DOCUMENT);
	private readonly meta = inject(Meta);
	private readonly titleService = inject(Title);
	private readonly translateDynamicService = inject(TranslateDynamicService);
	private readonly translate = inject(TranslateService);
	private readonly router = inject(Router);
	private readonly activatedRoute = inject(ActivatedRoute);

	/** Dernière route porteuse de `data.seo`, pour réappliquer au changement de langue. */
	private currentRouteData: { data: SeoRouteData; path: string } | null = null;

	/**
	 * Applique l'ensemble des balises d'une page : titre, description, canonical,
	 * Open Graph et Twitter Card.
	 *
	 * Passe par `Meta` et le `DOCUMENT` injecté, donc fonctionne pendant le rendu
	 * serveur : les crawlers qui ne lisent pas le JavaScript voient les balises.
	 */
	setPage(page: SeoPage): void {
		const url = this.absoluteUrl(page.path);
		const image = page.image || this.absoluteUrl(DEFAULT_IMAGE_PATH);
		const description = SeoService.truncate(page.description);
		const title = page.title.startsWith(TITLE_PREFIX) ? page.title : `${TITLE_PREFIX} - ${page.title}`;
		const lang = this.translateDynamicService.getLang() ?? 'fr';

		this.titleService.setTitle(title);

		this.upsertName('description', description);
		this.upsertName('robots', page.noindex ? 'noindex, nofollow' : 'index, follow');

		this.upsertProperty('og:title', title);
		this.upsertProperty('og:description', description);
		this.upsertProperty('og:type', page.type ?? 'website');
		this.upsertProperty('og:url', url);
		this.upsertProperty('og:image', image);
		this.upsertProperty('og:site_name', SITE_NAME);
		this.upsertProperty('og:locale', lang === 'en' ? 'en_US' : 'fr_FR');

		if (page.publishedTime) {
			this.upsertProperty('article:published_time', page.publishedTime);
		} else {
			this.meta.removeTag("property='article:published_time'");
		}

		this.upsertName('twitter:card', 'summary_large_image');
		this.upsertName('twitter:title', title);
		this.upsertName('twitter:description', description);
		this.upsertName('twitter:image', image);

		this.setCanonical(url);
		this.setAlternateLinks(url);

		// <html lang> est figé à "fr" dans index.html : sans ça, une page servie en
		// anglais serait annoncée comme française aux moteurs et aux lecteurs d'écran.
		this.document.documentElement?.setAttribute('lang', lang === 'en' ? 'en' : 'fr');
	}

	/**
	 * Applique les balises des routes qui déclarent `data.seo`, à chaque navigation
	 * et à chaque changement de langue. À appeler une seule fois, depuis AppComponent.
	 *
	 * Les routes sans `data.seo` sont ignorées : leurs composants posent eux-mêmes
	 * leurs balises depuis les données chargées, et ne doivent pas être écrasés.
	 */
	watchRouterData(destroyRef: DestroyRef): void {
		this.router.events
			.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntilDestroyed(destroyRef))
			.subscribe(event => {
				const data = SeoService.deepestSeoData(this.activatedRoute);
				this.currentRouteData = data ? { data, path: event.urlAfterRedirects.split('?')[0] } : null;
				this.applyRouteData();
			});

		this.translate.onLangChange
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe(() => this.applyRouteData());
	}

	private applyRouteData(): void {
		if (!this.currentRouteData) return;
		const { data, path } = this.currentRouteData;
		this.setPage({
			title: this.translate.instant(data.titleKey),
			description: this.translate.instant(data.descriptionKey),
			path,
			noindex: data.noindex,
		});
	}

	/** Descend jusqu'à la route active la plus profonde et retourne son `data.seo`. */
	private static deepestSeoData(route: ActivatedRoute): SeoRouteData | null {
		let current: ActivatedRoute | null = route;
		let found: SeoRouteData | null = null;
		while (current) {
			const seo = current.snapshot.data?.['seo'] as SeoRouteData | undefined;
			if (seo) found = seo;
			current = current.firstChild;
		}
		return found;
	}

	/**
	 * Applique les balises, puis les réapplique à chaque changement de langue.
	 * `factory` est rappelée à ce moment-là, donc les libellés traduits suivent.
	 */
	watchPage(factory: () => SeoPage, destroyRef: DestroyRef): void {
		this.setPage(factory());
		this.translate.onLangChange
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe(() => this.setPage(factory()));
	}

	/** Transforme un chemin en URL absolue sur le domaine canonique. */
	absoluteUrl(path: string): string {
		const base = environment.siteUrl.replace(/\/+$/, '');
		if (!path || path === '/') return base || '/';
		return `${base}/${path.replace(/^\/+/, '')}`;
	}

	/** Construit l'URL absolue d'un média stocké sur Minio. */
	mediaUrl(key: string | undefined | null): string | undefined {
		if (!key) return undefined;
		if (/^https?:\/\//i.test(key)) return key;
		return `${environment.minioBaseUrl.replace(/\/+$/, '')}/${key.replace(/^\/+/, '')}`;
	}

	/** Nettoie un contenu HTML pour en faire une description exploitable. */
	static fromHtml(html: string | undefined | null): string {
		if (!html) return '';
		return html
			.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
			.replace(/<[^>]*>/g, ' ')
			.replace(/&nbsp;/gi, ' ')
			.replace(/&amp;/gi, '&')
			.replace(/&(l|g)t;/gi, m => (m.toLowerCase() === '&lt;' ? '<' : '>'))
			.replace(/\s+/g, ' ')
			.trim();
	}

	private static truncate(value: string): string {
		const clean = value.replace(/\s+/g, ' ').trim();
		if (clean.length <= MAX_DESCRIPTION_LENGTH) return clean;
		// Coupe sur un mot entier plutôt qu'au milieu.
		const cut = clean.slice(0, MAX_DESCRIPTION_LENGTH);
		const lastSpace = cut.lastIndexOf(' ');
		return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
	}

	private upsertName(name: string, content: string): void {
		this.meta.updateTag({ name, content }, `name='${name}'`);
	}

	private upsertProperty(property: string, content: string): void {
		this.meta.updateTag({ property, content }, `property='${property}'`);
	}

	/**
	 * Met à jour l'unique `<link rel="canonical">`.
	 *
	 * Le site est servi sur quatre domaines (teampeps.fr/.com, avec et sans www) :
	 * sans canonical, les moteurs voient quatre copies du même contenu et en
	 * répartissent le crédit. Ici toutes les variantes pointent vers `siteUrl`.
	 */
	private setCanonical(url: string): void {
		const head = this.document.head;
		if (!head) return;

		let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
		if (!link) {
			link = this.document.createElement('link');
			link.setAttribute('rel', 'canonical');
			head.appendChild(link);
		}
		link.setAttribute('href', url);
	}

	/**
	 * Pose les balises `<link rel="alternate" hreflang="…">` (fr, en, x-default).
	 *
	 * Il n'existe pas d'URL distincte par langue ici : le contenu est le même
	 * chemin, traduit côté client selon le choix de l'utilisateur. On déclare
	 * donc les trois variantes vers cette même URL, pour indiquer explicitement
	 * à Google que la page existe en français et en anglais et éviter qu'il
	 * ignore l'anglais faute de variante annoncée.
	 */
	private setAlternateLinks(url: string): void {
		this.upsertAlternate(url, 'fr');
		this.upsertAlternate(url, 'en');
		this.upsertAlternate(url, 'x-default');
	}

	private upsertAlternate(href: string, hreflang: string): void {
		const head = this.document.head;
		if (!head) return;

		let link = head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
		if (!link) {
			link = this.document.createElement('link');
			link.setAttribute('rel', 'alternate');
			link.setAttribute('hreflang', hreflang);
			head.appendChild(link);
		}
		link.setAttribute('href', href);
	}
}
